import { ApiErrorDisplay } from '@core/ApiErrorDisplay';
import { AxiosErrorResponse } from '@core/error.models';
import {
  Column,
  ComponentType,
  createTableCell,
  createTableColumn,
  Form,
  FormSelect,
  Label,
  LoadingButton,
  PaginatedTable,
  requiredRule,
  UseFormReturn,
} from '@gcl/react-core';
import { IProjectMasterScheduleOptionWithRoles } from '@masterSchedule/masterSchedule.models';
import { useGetMasterScheduleOptionsForTransmittal } from '@masterSchedule/masterSchedule.queries';
import { useAllowNewIceT } from '@transmittal/checkerTransmittal/checkerTransmittal.hooks';
import { useAllowNewDT } from '@transmittal/designerTransmittal/designerTransmittal.hooks';
import { useAllowNewLir } from '@transmittal/lambethInternalReview/lambethInternalReview.hooks';
import { useAllowNewST } from '@transmittal/siteTransmittal/siteTransmittal.hooks';
import {
  ETransmittalType,
  TransmittalTypeOptionDT,
  TransmittalTypeOptionIceT,
  TransmittalTypeOptionLir,
  TransmittalTypeOptionST,
} from '@transmittal/transmittal.constants';
import { IProjectTransmittalOption, ITransmittalTypeOption } from '@transmittal/transmittal.models';
import {
  useFetchTransmittalSiteTransmittalForCheckerOptions,
  useFetchTransmittalSiteTransmittalForReviewerOptions,
} from '@transmittal/transmittal.queries';
import * as React from 'react';
import { transmittalCreationFormKeys } from './transmittalCreationForm.models';
import './TransmittalCreationForm.scss';
import { createTableLinkWithOpenNewTabColumn } from '@common/tableColumn.services';
import { useNavigate } from '@routes/routes.hooks';
import { routes } from '@routes/routes.models';
import { getTransmittalItemRoute } from '@transmittal/detailPage/transmittalDetailPage.hooks';

export interface ITransmittalCreationFormProps {
  projectId: number;
  useFormMethods: UseFormReturn;
  isCreateLoading: boolean;
  isCreateError: boolean;
  isCreateSuccess: boolean;
  createError: AxiosErrorResponse | null;
  createDraftTransmittal: (masterScheduleId: number, siteTransmittalId?: number) => void;
}

export const TransmittalCreationForm: React.FunctionComponent<ITransmittalCreationFormProps> = ({
  projectId,
  useFormMethods,
  isCreateLoading,
  isCreateError,
  isCreateSuccess,
  createError,
  createDraftTransmittal,
}: ITransmittalCreationFormProps) => {
  const { navigate, generateRoutePath } = useNavigate();
  const transmittalTypeOptions = getTransmittalTypeOptions();
  const transmittalTypeOption = useFormMethods.watch(transmittalCreationFormKeys.TYPE);
  const [selectedT1, setSelectedT1] = React.useState<IProjectMasterScheduleOptionWithRoles | undefined>(undefined);
  const [selectedST, setSelectedST] = React.useState<IProjectTransmittalOption | undefined>(undefined);
  const {
    data: dataMasterScheduleOptions,
    isFetching,
    isPaused,
    isError,
    error: errorGetMasterScheduleOptions,
  } = useGetMasterScheduleOptionsForTransmittal(
    {
      projectId,
      transmittalType: !!transmittalTypeOption && !!transmittalTypeOption.code ? transmittalTypeOption.code : undefined,
    },
    transmittalTypeOption.code !== ETransmittalType.ICE,
  );

  const { data: siteTransmittalOptionsForChecker, error: errorSiteTransmittalOptionsForChecker } =
    useFetchTransmittalSiteTransmittalForCheckerOptions({ projectId });
  const { data: siteTransmittalOptionsForReviewer, error: errorSiteTransmittalOptionsForReviewer } =
    useFetchTransmittalSiteTransmittalForReviewerOptions({
      projectId,
    });

  const masterScheduleOptions = React.useMemo(() => {
    if (transmittalTypeOption.code === ETransmittalType.DESIGNER)
      return dataMasterScheduleOptions?.filter((option) => option.isNominatedDesigner);
    if (transmittalTypeOption.code === ETransmittalType.SITE)
      return dataMasterScheduleOptions?.filter(
        (option) => option.isNominatedSrp || option.isNominatedTwc || option.isNominatedTwcm,
      );
    return [];
  }, [JSON.stringify(dataMasterScheduleOptions)]);

  const t1TableColumns = React.useMemo(
    () => [
      createTableColumn({
        disableSortBy: true,
        accessor: 'id',
        Header: '',
        disableResizing: true,
        Cell: createTableCell<number, IProjectMasterScheduleOptionWithRoles>(
          (
            value: number,
            item: IProjectMasterScheduleOptionWithRoles,
            index: number,
            cellExtras: { [key: string]: any },
          ) => {
            const isCurrentT1 = cellExtras.selectedT1?.id === item.id || false;
            return (
              <LoadingButton
                data-testid={`btnSave-${item.refNo}`}
                type={ComponentType.Success}
                onClick={() => {
                  if (cellExtras.setSelectedT1) cellExtras.setSelectedT1(item);
                  createDraftTransmittal(item.id);
                }}
                isLoading={isCurrentT1 ? cellExtras.isCreateLoading : false}
                isError={isCurrentT1 ? cellExtras.isCreateError : false}
                isSuccess={isCurrentT1 ? cellExtras.isCreateSuccess : false}
                timeToResetSuccessState={3000}
                timeToResetErrorState={5000}
              >
                Save
              </LoadingButton>
            );
          },
        ),
      } as Column),
      createTableLinkWithOpenNewTabColumn<IProjectMasterScheduleOptionWithRoles>(
        'refNo',
        'T1 Ref No',
        ({ item }) => generateRoutePath(routes.t1.view.path, { id: item.id?.toString() }),
        ({ value, item, index, cellExtras }, link) => link,
      ),
      createTableColumn({
        Header: 'Design Package',
        accessor: 'designPackage',
        disableSortBy: true,
        disableResizing: true,
      } as Column),
    ],
    [],
  );

  const transmittalTableColumns = React.useMemo(
    () => [
      createTableColumn({
        disableSortBy: true,
        accessor: 'id',
        Header: '',
        disableResizing: true,
        Cell: createTableCell<number, IProjectTransmittalOption>(
          (value: number, item: IProjectTransmittalOption, index: number, cellExtras: { [key: string]: any }) => {
            const selectedSTLocal = cellExtras.selectedST as IProjectTransmittalOption | undefined;
            const isCurrentT1 = selectedSTLocal?.id === item.id || false;
            return (
              <LoadingButton
                type={ComponentType.Success}
                onClick={() => {
                  if (cellExtras.setSelectedST) cellExtras.setSelectedST(item);
                  createDraftTransmittal(item.masterScheduleId, item.id);
                }}
                isLoading={isCurrentT1 ? cellExtras.isCreateLoading : false}
                isError={isCurrentT1 ? cellExtras.isCreateError : false}
                isSuccess={isCurrentT1 ? cellExtras.isCreateSuccess : false}
                timeToResetSuccessState={3000}
                timeToResetErrorState={5000}
              >
                Save
              </LoadingButton>
            );
          },
        ),
      } as Column),
      createTableLinkWithOpenNewTabColumn<IProjectTransmittalOption>(
        'transmittalNo',
        'Transmittal No',
        ({ item }) => generateRoutePath(getTransmittalItemRoute(item.type), { id: item.id?.toString() }),
        ({ value, item, index, cellExtras }, link) => link,
      ),
      createTableLinkWithOpenNewTabColumn<IProjectTransmittalOption>(
        'masterSchedule.refNo',
        'T1 Ref No',
        ({ item }) => generateRoutePath(routes.t1.view.path, { id: item.masterScheduleId?.toString() }),
        ({ value, item, index, cellExtras }, link) => link,
      ),
      createTableColumn({
        Header: 'Design Package',
        accessor: 'masterSchedule.designPackage',
        disableSortBy: true,
        disableResizing: true,
      } as Column),
    ],
    [],
  );
  return (
    <>
      <ApiErrorDisplay
        setFormError={useFormMethods.setError}
        errors={[
          { error: createError, genericError: `Create ${transmittalTypeOption.name} failed` },
          { error: errorGetMasterScheduleOptions, genericError: 'Get master schedule options failed' },
          {
            error: errorSiteTransmittalOptionsForChecker,
            genericError: 'Get site tranmisttal options for checker transmittal failed',
          },
          {
            error: errorSiteTransmittalOptionsForReviewer,
            genericError: 'Get site tranmisttal options for reviewer transmittal failed',
          },
        ]}
      />
      <Form
        useFormMethods={useFormMethods}
        render={(formFieldMethods) => {
          return (
            <div>
              <Label labelFor={transmittalCreationFormKeys.TYPE}>Transmittal Type</Label>
              <FormSelect
                allowSearch={false}
                clearable={false}
                options={transmittalTypeOptions}
                id={transmittalCreationFormKeys.TYPE}
                formFieldMethods={formFieldMethods}
                rules={requiredRule}
                displayFieldName="name"
              />
              {transmittalTypeOption.code !== ETransmittalType.ICE &&
                transmittalTypeOption.code !== ETransmittalType.REVIEWER &&
                masterScheduleOptions &&
                masterScheduleOptions.length > 0 && (
                  <div className="gcl-gems-transmittal-creation-form-t1-table">
                    <PaginatedTable
                      classNames={{ row: 'gcl-gems-transmittal-creation-form-t1-table-row' }}
                      columns={t1TableColumns}
                      items={masterScheduleOptions}
                      disablePagination
                      allowSelectRow={false}
                      cellExtras={{ selectedT1, setSelectedT1, isCreateLoading, isCreateError, isCreateSuccess }}
                    />
                  </div>
                )}
              {transmittalTypeOption.code === ETransmittalType.ICE &&
                siteTransmittalOptionsForChecker &&
                siteTransmittalOptionsForChecker?.length > 0 && (
                  <div className="gcl-gems-transmittal-creation-form-t1-table">
                    <PaginatedTable
                      classNames={{ row: 'gcl-gems-transmittal-creation-form-t1-table-row' }}
                      columns={transmittalTableColumns}
                      items={siteTransmittalOptionsForChecker ?? []}
                      disablePagination
                      allowSelectRow={false}
                      cellExtras={{ selectedST, setSelectedST, isCreateLoading, isCreateError, isCreateSuccess }}
                    />
                  </div>
                )}
              {transmittalTypeOption.code === ETransmittalType.REVIEWER &&
                siteTransmittalOptionsForReviewer &&
                siteTransmittalOptionsForReviewer?.length > 0 && (
                  <div className="gcl-gems-transmittal-creation-form-t1-table">
                    <PaginatedTable
                      classNames={{ row: 'gcl-gems-transmittal-creation-form-t1-table-row' }}
                      columns={transmittalTableColumns}
                      items={siteTransmittalOptionsForReviewer ?? []}
                      disablePagination
                      allowSelectRow={false}
                      cellExtras={{ selectedST, setSelectedST, isCreateLoading, isCreateError, isCreateSuccess }}
                    />
                  </div>
                )}
            </div>
          );
        }}
      />
    </>
  );
};

const getTransmittalTypeOptions: () => ITransmittalTypeOption[] = () => {
  const isAllowNewDT = useAllowNewDT();
  const isAllowNewST = useAllowNewST();
  const isAllowNewIceT = useAllowNewIceT();
  const isAllowNewLir = useAllowNewLir();

  const options: ITransmittalTypeOption[] = [];
  if (isAllowNewDT) options.push(TransmittalTypeOptionDT);
  if (isAllowNewST) options.push(TransmittalTypeOptionST);
  if (isAllowNewIceT) options.push(TransmittalTypeOptionIceT);
  if (isAllowNewLir) options.push(TransmittalTypeOptionLir);

  return options;
};
